import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { FileUpload } from "@/components/FileUpload";
import { StudioLayout } from "@/components/StudioLayout";
import { useBrandAccent } from "@/context/BrandContext";
import { CHANNELS, STATUSES, POST_TYPES, MONTHS, WEEKDAYS, channelMeta, statusMeta } from "@/lib/constants";
import { ChannelIcon } from "@/components/ChannelIcon";
import { StatusDot } from "@/components/StatusDot";
import { MediaThumb } from "@/components/MediaThumb";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Plus, Search, Send, Printer, X, Grid3x3, LayoutGrid, List as ListIcon, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { todayMonth, todayStr, fmtDate } from "@/lib/format";

export default function BrandCalendar() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { setAccent } = useBrandAccent();
  const [brand, setBrand] = useState(null);
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState("mes");
  const [month, setMonth] = useState(todayMonth());
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createDate, setCreateDate] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get(`/brands/${brandId}`).then((r) => { setBrand(r.data); setAccent(r.data.color); }); api.get(`/brands/${brandId}/members`).then((r) => setMembers(r.data)); }, [brandId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [brandId, month, channel, status, search, showArchived]);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (!search) params.month = month;
    if (channel) params.channel = channel;
    if (status) params.status = status;
    if (search) params.search = search;
    if (showArchived) params.archived = "true";
    const { data } = await api.get(`/brands/${brandId}/posts`, { params });
    setPosts(data);
    setLoading(false);
  };

  const shiftMonth = (dir) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const sendMonthPlan = async () => {
    try {
      const { data } = await api.post(`/brands/${brandId}/send-month-plan`, { month });
      toast.success(data.sent > 0 ? `Enviado al cliente: ${data.sent} piezas` : "No hay piezas en revisión interna este mes.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [my, mm] = month.split("-").map(Number);
  const monthLabel = `${MONTHS[mm - 1]} ${my}`;

  return (
    <StudioLayout brand={brand}>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4 mb-6 no-print">
        <div className="flex items-center gap-3">
          <button onClick={() => shiftMonth(-1)} data-testid="prev-month" className="w-8 h-8 border border-line rounded-sm flex items-center justify-center"><ChevronLeft size={16} /></button>
          <h1 className="font-display text-3xl sm:text-4xl min-w-[220px]">{search ? "Resultados" : monthLabel}</h1>
          <button onClick={() => shiftMonth(1)} data-testid="next-month" className="w-8 h-8 border border-line rounded-sm flex items-center justify-center"><ChevronRight size={16} /></button>
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="month-picker" className="border border-line rounded-sm px-2 py-1.5 text-sm font-data bg-surface ml-1" />
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="flex border border-line rounded-sm overflow-hidden">
            <ViewBtn active={view === "mes"} onClick={() => setView("mes")} testid="view-mes" icon={Grid3x3} label="Mes" />
            <ViewBtn active={view === "feed"} onClick={() => setView("feed")} testid="view-feed" icon={LayoutGrid} label="Feed" />
            <ViewBtn active={view === "lista"} onClick={() => setView("lista")} testid="view-lista" icon={ListIcon} label="Lista" />
          </div>
          <button onClick={() => setSharing(true)} data-testid="share-client" className="flex items-center gap-1.5 border border-line rounded-sm px-3 py-2 text-sm"><Share2 size={15} /> Compartir con cliente</button>
          <button onClick={() => window.print()} data-testid="export-pdf" className="flex items-center gap-1.5 border border-line rounded-sm px-3 py-2 text-sm"><Printer size={15} /> PDF</button>
          <button onClick={sendMonthPlan} data-testid="send-month-plan" className="flex items-center gap-1.5 border border-line rounded-sm px-3 py-2 text-sm"><Send size={15} /> Enviar plan del mes</button>
          <button onClick={() => { setCreateDate(null); setCreating(true); }} data-testid="create-post-btn" className="flex items-center gap-1.5 px-3 py-2 text-white text-sm rounded-sm" style={{ background: "var(--brand-color)" }}><Plus size={15} /> Crear post</button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6 no-print">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-subtle" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar texto…" data-testid="search-input" className="border border-line rounded-sm pl-8 pr-3 py-2 text-sm bg-surface w-48" />
        </div>
        <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="filter-channel" className="border border-line rounded-sm px-3 py-2 text-sm bg-surface">
          <option value="">Todos los canales</option>
          {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="border border-line rounded-sm px-3 py-2 text-sm bg-surface">
          <option value="">Todos los estados</option>
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-subtle">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived" /> Ver archivados
        </label>
      </div>

      {/* Print header */}
      <div className="hidden print:block mb-4">
        <h1 className="font-display text-3xl">{brand?.name} · {monthLabel}</h1>
      </div>

      {loading ? (
        <div className="py-20 text-center text-subtle">Cargando…</div>
      ) : view === "mes" ? (
        <MonthView posts={posts} month={month} brand={brand} onOpen={(id) => navigate(`/brand/${brandId}/post/${id}`)} onMove={load} onCreate={(d) => { setCreateDate(d); setCreating(true); }} />
      ) : view === "feed" ? (
        <FeedView posts={posts} onOpen={(id) => navigate(`/brand/${brandId}/post/${id}`)} />
      ) : (
        <ListView posts={posts} brand={brand} onOpen={(id) => navigate(`/brand/${brandId}/post/${id}`)} />
      )}

      {posts.length === 0 && !loading && (
        <div className="border border-dashed border-line bg-surface py-16 text-center mt-2">
          <p className="font-display text-2xl mb-2">{search ? "Nada coincide" : "Este mes está vacío"}</p>
          <p className="text-subtle text-sm mb-5">{search ? "Prueba con otras palabras." : "Crea el primer post para empezar a componer la pared."}</p>
          {!search && <button onClick={() => { setCreateDate(null); setCreating(true); }} className="px-4 py-2.5 bg-ink text-white text-sm rounded-sm">Crear post</button>}
        </div>
      )}

      {creating && <CreatePostModal brandId={brandId} brand={brand} month={month} initialDate={createDate} onClose={() => setCreating(false)} onCreated={(p) => { setCreating(false); navigate(`/brand/${brandId}/post/${p.id}`); }} />}
      {sharing && <ShareModal members={members} brandId={brandId} month={month} onClose={() => setSharing(false)} />}
    </StudioLayout>
  );
}

function ShareModal({ members, brandId, month, onClose }) {
  const [copied, setCopied] = useState(null);
  const [shareMonth, setShareMonth] = useState(month || todayMonth());
  const navigate = useNavigate();
  const copy = (token, id) => { navigator.clipboard.writeText(`${window.location.origin}/c/${token}?m=${shareMonth}`); setCopied(id); setTimeout(() => setCopied(null), 1500); };
  const [sy, sm] = shareMonth.split("-").map(Number);
  return (
    <div className="fixed inset-0 bg-ink/30 z-50 flex items-center justify-center p-4 no-print" onClick={onClose}>
      <div className="bg-surface border border-line w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
        <div className="flex items-center justify-between mb-2"><h2 className="font-display text-2xl">Compartir calendario</h2><button onClick={onClose}><X size={18} /></button></div>
        <p className="text-subtle text-sm mb-5">Cada miembro tiene un enlace único y sin contraseña. Verá el calendario de su marca y podrá comentar, aprobar o pedir cambios.</p>
        <div className="mb-5 border border-line rounded-sm p-3 bg-app">
          <label className="text-sm font-medium">Mes que verá el cliente</label>
          <p className="text-xs text-subtle mb-2">El enlace abrirá directamente en <span className="text-ink">{MONTHS[sm - 1]} {sy}</span>.</p>
          <input type="month" value={shareMonth} min={todayMonth()} onChange={(e) => e.target.value && setShareMonth(e.target.value)} data-testid="share-month" className="border border-line rounded-sm px-2 py-1.5 text-sm font-data bg-surface w-full" />
        </div>
        {members.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-subtle mb-4">Aún no hay miembros del cliente. Añádelos en la ficha de la marca.</p>
            <button onClick={() => navigate(`/brand/${brandId}`)} data-testid="share-goto-brand" className="px-4 py-2.5 bg-ink text-white text-sm rounded-sm">Ir a la ficha de la marca</button>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 border border-line rounded-sm p-3">
                <span style={{ width: 8, height: 8, background: m.active ? "#3F6B4F" : "#6E6E73", borderRadius: "50%" }} />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{m.name}</div><div className="text-xs text-subtle truncate">{m.email}</div></div>
                <button onClick={() => copy(m.token, m.id)} disabled={!m.active} data-testid={`share-copy-${m.id}`} className="text-xs flex items-center gap-1 border border-line rounded-sm px-2 py-1 disabled:opacity-40">{copied === m.id ? <Check size={12} /> : <Copy size={12} />} {copied === m.id ? "Copiado" : "Copiar enlace"}</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ViewBtn({ active, onClick, testid, icon: Icon, label }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`flex items-center gap-1.5 px-3 py-2 text-sm ${active ? "bg-ink text-white" : "bg-surface text-subtle"}`}>
      <Icon size={14} /> {label}
    </button>
  );
}

// ---------------- Month (contact sheet) ----------------
function MonthView({ posts, month, brand, onOpen, onMove, onCreate }) {
  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const daysInMonth = new Date(y, m, 0).getDate();
  const startWeekday = (first.getDay() + 6) % 7; // Monday=0

  const byDay = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      const d = Number(p.date.split("-")[2]);
      (map[d] = map[d] || []).push(p);
    });
    return map;
  }, [posts]);

  const drop = async (day, e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("postId");
    if (!id) return;
    const date = `${month}-${String(day).padStart(2, "0")}`;
    await api.patch(`/posts/${id}/date`, { date });
    onMove();
  };

  const today = todayStr();
  const pastDay = (day) => day && `${month}-${String(day).padStart(2, "0")}` < today;

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div data-testid="month-view">
      <div className="grid grid-cols-7 gap-px mb-1">
        {WEEKDAYS.map((w) => <div key={w} className="text-xs text-subtle font-data text-center py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-px bg-line border border-line">
        {cells.map((day, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: Math.min(i * 0.008, 0.4) }}
            className={`aspect-square bg-app relative group/cell ${day && !(byDay[day] || []).length && !pastDay(day) ? "cursor-pointer hover:bg-line/40" : ""} ${pastDay(day) && !(byDay[day] || []).length ? "opacity-50" : ""}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => day && !pastDay(day) && drop(day, e)}
            onClick={() => { if (day && !(byDay[day] || []).length && !pastDay(day)) onCreate(`${month}-${String(day).padStart(2, "0")}`); }}
            data-testid={day ? `day-cell-${day}` : undefined}
          >
            {day && (
              <>
                <span className="absolute top-1 left-1.5 font-data text-[11px] text-subtle z-10 mix-blend-difference text-white">{String(day).padStart(2, "0")}</span>
                {!(byDay[day] || []).length && !pastDay(day) && (
                  <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/cell:opacity-100 transition-opacity pointer-events-none">
                    <Plus size={18} className="text-subtle" />
                  </span>
                )}
                {(byDay[day] || []).map((p, idx) => {
                  const st = statusMeta(p.status);
                  return (
                    <button
                      key={p.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("postId", p.id)}
                      onClick={() => onOpen(p.id)}
                      data-testid={`month-post-${p.id}`}
                      className="absolute inset-0 group"
                      style={{ transform: `translate(${idx * 4}px, ${idx * 4}px)`, zIndex: idx + 1 }}
                    >
                      <MediaThumb post={p} />
                      <span className="absolute bottom-1 left-1 flex items-center gap-1">
                        <span className="bg-ink/60 rounded-sm p-0.5"><ChannelIcon channel={p.channel} size={11} className="text-white" /></span>
                      </span>
                      <span className="absolute bottom-1 right-1"><StatusDot color={st.dot} /></span>
                    </button>
                  );
                })}
                {!!(byDay[day] || []).length && !pastDay(day) && (
                  <button onClick={(e) => { e.stopPropagation(); onCreate(`${month}-${String(day).padStart(2, "0")}`); }} data-testid={`add-day-${day}`} className="absolute top-1 right-1 z-20 bg-surface/90 border border-line rounded-sm p-0.5 opacity-0 group-hover/cell:opacity-100 transition-opacity"><Plus size={12} /></button>
                )}
              </>
            )}
          </motion.div>
        ))}
      </div>
      <p className="text-xs text-subtle mt-3 no-print">Arrastra un post a otro día para cambiar su fecha.</p>
    </div>
  );
}

// ---------------- Feed ----------------
function FeedView({ posts, onOpen }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className="grid grid-cols-3 gap-1 max-w-3xl" data-testid="feed-view">
      {posts.map((p) => {
        const file = p.files?.[0];
        const st = statusMeta(p.status);
        return (
          <button key={p.id} onClick={() => onOpen(p.id)} data-testid={`feed-post-${p.id}`} className="aspect-square bg-app relative group overflow-hidden">
            {p.cover_url ? <img src={fileUrl(p.cover_url)} alt="" className="w-full h-full object-cover" />
              : file ? (file.type === "video" ? <video src={fileUrl(file.url)} muted preload="metadata" className="w-full h-full object-cover" /> : <img src={fileUrl(file.url)} alt="" className="w-full h-full object-cover" />)
              : <div className="w-full h-full flex items-center justify-center text-subtle text-sm">{p.type === "texto" ? "Aa" : "—"}</div>}
            <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <span className="flex items-center gap-1.5 text-white text-xs"><StatusDot color={st.dot} /> {st.label}</span>
            </div>
          </button>
        );
      })}
    </motion.div>
  );
}

// ---------------- List ----------------
function ListView({ posts, brand, onOpen }) {
  return (
    <div className="border border-line bg-surface" data-testid="list-view">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-subtle text-left">
            <th className="py-2.5 px-4 font-normal font-data text-xs">Fecha</th>
            <th className="py-2.5 px-2 font-normal font-data text-xs">Canal</th>
            <th className="py-2.5 px-2 font-normal">Texto</th>
            <th className="py-2.5 px-4 font-normal">Estado</th>
          </tr>
        </thead>
        <tbody>
          {posts.map((p) => {
            const st = statusMeta(p.status);
            return (
              <tr key={p.id} onClick={() => onOpen(p.id)} data-testid={`list-post-${p.id}`} className="border-b border-line last:border-0 cursor-pointer hover:bg-app">
                <td className="py-3 px-4 font-data text-xs whitespace-nowrap">{fmtDate(p.date)} · {p.time}</td>
                <td className="py-3 px-2"><ChannelIcon channel={p.channel} size={15} className="text-subtle" /></td>
                <td className="py-3 px-2 max-w-md truncate">{p.text ? p.text.slice(0, 90) : <span className="text-subtle">Sin texto</span>}</td>
                <td className="py-3 px-4"><span className="flex items-center gap-2 whitespace-nowrap"><StatusDot color={st.dot} /> {st.label}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------- Create post ----------------
function CreatePostModal({ brandId, brand, month, initialDate, onClose, onCreated }) {
  const channels = brand?.channels?.length ? brand.channels : CHANNELS.map((c) => c.key);
  const defaultDate = initialDate || (`${month}-01` < todayStr() ? todayStr() : `${month}-01`);
  const [form, setForm] = useState({ title: "", date: defaultDate, time: "09:00", channel: channels[0], type: "imagen_unica", text: "", hashtags: "", status: "borrador" });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/brands/${brandId}/posts`, form);
      for (const f of pendingFiles) {
        await api.post(`/posts/${data.id}/files`, { url: f.url, is_video: f.is_video, thumbnail: f.url });
      }
      onCreated(data); toast.success("Post creado");
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-ink/30 z-50 flex items-center justify-center p-4 no-print" onClick={onClose}>
      <div className="bg-surface border border-line w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-post-modal">
        <div className="flex items-center justify-between mb-5"><h2 className="font-display text-2xl">Nuevo post</h2><button onClick={onClose}><X size={18} /></button></div>
        <div className="space-y-4">
          <div>
            <span className="text-sm text-subtle">Foto o video</span>
            <p className="text-xs text-subtle mb-2">Sube el contenido ahora o hazlo después.</p>
            {pendingFiles.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-2">
                {pendingFiles.map((f, i) => <div key={i} className="aspect-square border border-line overflow-hidden rounded-sm"><img src={fileUrl(f.url)} alt="" className="w-full h-full object-cover" /></div>)}
              </div>
            )}
            <FileUpload onUploaded={(d) => setPendingFiles((p) => [...p, d])} />
          </div>
          <div className="flex gap-3">
            <F label="Fecha" className="flex-1"><input type="date" min={todayStr()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="post-date" className="inp font-data" /></F>
            <F label="Hora" className="w-28"><input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="inp font-data" /></F>
          </div>
          <p className="text-xs text-subtle -mt-2">Puedes cambiar la fecha a cualquier día o mes; el post se moverá solo en el calendario.</p>
          <div className="flex gap-3">
            <F label="Canal" className="flex-1"><select value={form.channel} onChange={(e) => { const ch = e.target.value; setForm({ ...form, channel: ch, type: ch === "instagram_reel" ? "video" : form.type }); }} data-testid="post-channel" className="inp">{channels.map((c) => <option key={c} value={c}>{channelMeta(c).label}</option>)}</select></F>
            {form.channel === "instagram_reel" ? (
              <F label="Tipo" className="flex-1"><div className="inp bg-app text-subtle">Reel (video)</div></F>
            ) : (
              <F label="Tipo" className="flex-1"><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="inp">{POST_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></F>
            )}
          </div>
          <button onClick={submit} disabled={busy} data-testid="submit-post" className="w-full py-2.5 bg-ink text-white text-sm rounded-sm disabled:opacity-50">Crear post</button>
        </div>
      </div>
    </div>
  );
}
function F({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="text-sm text-subtle">{label}</span><div className="mt-1">{children}</div></label>;
}
